import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Formik, Field, ErrorMessage, FieldProps } from 'formik';
import * as Yup from 'yup';
import { Button, Form, Grid, Header, Message, Segment } from 'semantic-ui-react';
import { ITrainCreateRequestValues } from '@relab/alstom-ptw-shared';
import i18n from 'i18next';

import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appState, appStateServerRequests } from '../../redux/selectors';
import { trainCreateAction } from '../../redux/actions';
import { TranslationComponent } from '../TranslationComponent';
import { useTranslation } from 'react-i18next';

const CreateTrainSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    name: Yup.string().min(2, t('Too Short!')).max(50, t('Too Long!')).required(t('Name is required')),
    vehicles: Yup.number().moreThan(0).required(t('Number of vehicles is required')),
  });

interface TrainCreateComponentProps {
  projectId: string;
  trainCreateAction: any;
  createTrainServerRequest?: ServerRequestStatus;
}

const PTrainCreateComponent = ({
  projectId,
  trainCreateAction,
  createTrainServerRequest,
}: TrainCreateComponentProps) => {
  const handleSubmit = (values: ITrainCreateRequestValues) => {
    trainCreateAction(projectId, values);
  };

  const { t } = useTranslation();

  const validationSchema = useMemo(() => CreateTrainSchema(t), [t]);

  const initialValues = {
    name: '',
    vehicles: 0,
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ handleSubmit, isSubmitting, resetForm }) => (
        <Form
          size="large"
          error
          loading={isSubmitting && createTrainServerRequest && createTrainServerRequest.pending}
          onSubmit={handleSubmit}
        >
          <Segment>
            <Header>
              <TranslationComponent stringKey={'New Train'} />
            </Header>

            <Grid columns="equal">
              <Grid.Row>
                <Grid.Column>
                  <Field name="name">
                    {({ field }: FieldProps) => <Form.Input placeholder={t('Train name')} fluid {...field} />}
                  </Field>
                  <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                </Grid.Column>
                <Grid.Column>
                  <Field name="vehicles">
                    {({ field }: FieldProps) => (
                      <Form.Input placeholder={t('Vehicles')} type="number" fluid {...field} />
                    )}
                  </Field>
                  <ErrorMessage name="vehicles">{(msg) => <Message error content={msg} />}</ErrorMessage>
                </Grid.Column>
                <Grid.Column>
                  <Button primary fluid size="large" type="submit">
                    <TranslationComponent stringKey={'Generate train'} />
                  </Button>
                  {createTrainServerRequest && createTrainServerRequest.success && (
                    <React.Fragment>
                      <Message content={t('Train created')} />
                      <Button fluid size="large" onClick={() => resetForm()}>
                        Reset
                      </Button>
                    </React.Fragment>
                  )}
                  {createTrainServerRequest && createTrainServerRequest.error && (
                    <Message error content={createTrainServerRequest.error} />
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => ({
  projectId: appState(state).project._id,
  createTrainServerRequest: appStateServerRequests(state, ActionType.TRAIN_CREATE),
});

export const TrainCreateComponent = connect(mapStateToProps, {
  trainCreateAction,
})(PTrainCreateComponent);
